import { useEffect, useRef } from "react";
import { APIProvider, Map, useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import { Satellite } from "lucide-react";
import RoofPdfView, { hasPdfBg } from "./RoofPdfView";

const FALDA_COLORS = ["#FDD003", "#22D3EE", "#C084FC", "#4ADE80", "#FB923C", "#F472B6", "#A3E635", "#2DD4BF"];
const ANNOT = {
  cabina: { color: "#F97316", label: "Cabina elettrica" },
  cavidotto: { color: "#38BDF8", label: "Cavidotti" },
  locale_tecnico: { color: "#A855F7", label: "Locale tecnico / inverter" },
  parcheggio: { color: "#22C55E", label: "Area parcheggio mezzi di sollevamento" },
};

// OverlayView che posiziona la foto (top-down) del pannello sul singolo modulo,
// mappando il quadrato immagine sul quadrilatero del pannello (rotazione+scala via matrix CSS).
function makePanelImageOverlay(g, corners, photoUrl) {
  const ov = new g.maps.OverlayView();
  const c0 = corners[0], c1 = corners[1], c3 = corners[3];
  ov.onAdd = function () {
    const img = document.createElement("img");
    img.src = photoUrl; img.draggable = false;
    img.style.position = "absolute"; img.style.transformOrigin = "0 0";
    img.style.pointerEvents = "none"; img.style.userSelect = "none";
    img.style.boxSizing = "border-box";
    img.style.border = "0.125px solid rgba(255,255,255,0.28)";
    img.style.filter = "saturate(1.2) contrast(1.12) brightness(0.92) drop-shadow(0 1px 1px rgba(0,0,0,0.5))";
    this._img = img;
    (this.getPanes().markerLayer || this.getPanes().overlayLayer).appendChild(img);
  };
  ov.draw = function () {
    const proj = this.getProjection(); if (!proj || !this._img) return;
    const p0 = proj.fromLatLngToDivPixel(new g.maps.LatLng(c0.lat, c0.lng));
    const p1 = proj.fromLatLngToDivPixel(new g.maps.LatLng(c1.lat, c1.lng));
    const p3 = proj.fromLatLngToDivPixel(new g.maps.LatLng(c3.lat, c3.lng));
    if (!p0 || !p1 || !p3) return;
    const B = 100;
    const a = (p1.x - p0.x) / B, b = (p1.y - p0.y) / B, c = (p3.x - p0.x) / B, d = (p3.y - p0.y) / B;
    const s = this._img.style;
    s.width = B + "px"; s.height = B + "px";
    s.transform = `matrix(${a}, ${b}, ${c}, ${d}, ${p0.x}, ${p0.y})`;
  };
  ov.onRemove = function () { if (this._img && this._img.parentNode) this._img.parentNode.removeChild(this._img); this._img = null; };
  return ov;
}

// Esclusione (area rossa) disegnata come SVG nel floatPane → sempre SOPRA i moduli.
function makeExclusionOverlay(g, path) {
  const ov = new g.maps.OverlayView();
  const SVGNS = "http://www.w3.org/2000/svg";
  ov.onAdd = function () {
    const svg = document.createElementNS(SVGNS, "svg");
    svg.style.position = "absolute"; svg.style.left = "0"; svg.style.top = "0";
    svg.style.overflow = "visible"; svg.style.pointerEvents = "none";
    const poly = document.createElementNS(SVGNS, "polygon");
    poly.setAttribute("fill", "rgba(220,38,38,0.35)");
    poly.setAttribute("stroke", "#DC2626");
    poly.setAttribute("stroke-width", "2");
    svg.appendChild(poly);
    this._svg = svg; this._poly = poly;
    this.getPanes().floatPane.appendChild(svg);
  };
  ov.draw = function () {
    const proj = this.getProjection(); if (!proj || !this._poly) return;
    const pts = path.map((c) => {
      const p = proj.fromLatLngToDivPixel(new g.maps.LatLng(c.lat, c.lng));
      return `${p.x},${p.y}`;
    }).join(" ");
    this._poly.setAttribute("points", pts);
  };
  ov.onRemove = function () { if (this._svg && this._svg.parentNode) this._svg.parentNode.removeChild(this._svg); this._svg = null; this._poly = null; };
  return ov;
}

function RoofPolys({ falde, panelLayout, annotations, panelPhoto, pdfExport }) {
  const map = useMap();
  const geometry = useMapsLibrary("geometry");
  const polysRef = useRef([]);

  useEffect(() => {
    if (!map || !window.google) return;
    const g = window.google;
    polysRef.current.forEach((p) => p.setMap(null));
    polysRef.current = [];
    const bounds = new g.maps.LatLngBounds();
    const drawPanels = () => {
      if (!panelLayout?.enabled) return;
      const list = panelLayout.panels || [];
      const usePhoto = !!panelPhoto && list.length <= 300;
      list.forEach((pn) => {
        const path = (pn.corners || []).map((c) => ({ lat: c.lat, lng: c.lng }));
        if (path.length < 3) return;
        if (usePhoto && path.length >= 4) {
          const ovImg = makePanelImageOverlay(g, path, panelPhoto);
          ovImg.setMap(map);
          polysRef.current.push(ovImg);
        } else {
          const pp = new g.maps.Polygon({
            paths: path, map, editable: false, clickable: false,
            strokeColor: "#0B1B4D", strokeWeight: 1, fillColor: "#1E3A8A", fillOpacity: 0.82, zIndex: 8,
          });
          polysRef.current.push(pp);
        }
      });
    };
    (falde || []).forEach((f, idx) => {
      const path = (f.coordinates || []).map((c) => ({ lat: c.lat, lng: c.lng }));
      if (path.length < 3) return;
      const color = FALDA_COLORS[idx % FALDA_COLORS.length];
      const poly = new g.maps.Polygon({
        paths: path, map, editable: false, clickable: false,
        strokeColor: color, strokeWeight: 2.5, fillColor: color, fillOpacity: 0.22, zIndex: 5,
      });
      polysRef.current.push(poly);
      path.forEach((p) => bounds.extend(p));
      (f.esclusioni || []).forEach((e) => {
        const ep = (e.coordinates || []).map((c) => ({ lat: c.lat, lng: c.lng }));
        if (ep.length < 3) return;
        const ex = makeExclusionOverlay(g, ep);
        ex.setMap(map);
        polysRef.current.push(ex);
      });
    });
    drawPanels();
    (annotations || []).forEach((a) => {
      const meta = ANNOT[a.tipo]; if (!meta) return;
      const path = (a.coordinates || []).map((c) => ({ lat: c.lat, lng: c.lng })).filter((c) => c.lat != null && c.lng != null);
      if (!path.length) return;
      if ((a.tipo === "cabina" || a.tipo === "locale_tecnico") && path.length >= 3) {
        polysRef.current.push(new g.maps.Polygon({ paths: path, map, editable: false, clickable: false, strokeColor: meta.color, strokeWeight: 2, fillColor: meta.color, fillOpacity: 0.28, zIndex: 7 }));
      } else if (a.tipo === "cavidotto" && path.length >= 2) {
        polysRef.current.push(new g.maps.Polyline({ path, map, clickable: false, strokeColor: meta.color, strokeWeight: 4, zIndex: 7 }));
      } else if (a.tipo === "parcheggio") {
        polysRef.current.push(new g.maps.Marker({ position: path[0], map, clickable: false, zIndex: 8, icon: { path: g.maps.SymbolPath.CIRCLE, scale: 8, fillColor: meta.color, fillOpacity: 1, strokeColor: "#fff", strokeWeight: 2 } }));
      }
      path.forEach((p) => bounds.extend(p));
      polysRef.current.push(new g.maps.Marker({ position: path[0], map, clickable: false, zIndex: 9, icon: { path: g.maps.SymbolPath.CIRCLE, scale: 0.01, fillOpacity: 0, strokeOpacity: 0 }, label: { text: a.label || meta.label, color: meta.color, fontWeight: "bold", fontSize: "12px" } }));
    });
    const signalReady = () => {
      try { window.__roofMapReady = "1"; if (document.body) document.body.setAttribute("data-roofmap-ready", "1"); } catch (e) { /* noop */ }
    };
    if (!bounds.isEmpty()) {
      map.fitBounds(bounds, 48);
      const l = g.maps.event.addListenerOnce(map, "idle", () => {
        if (map.getZoom() > 21) map.setZoom(21);
        if (pdfExport) {
          g.maps.event.addListenerOnce(map, "tilesloaded", () => { setTimeout(signalReady, 900); });
          setTimeout(signalReady, 4000); // fallback: segnala comunque anche se tilesloaded non scatta
        }
      });
      return () => { g.maps.event.removeListener(l); polysRef.current.forEach((p) => p.setMap(null)); polysRef.current = []; };
    }
    if (pdfExport) setTimeout(signalReady, 2500);
    return () => { polysRef.current.forEach((p) => p.setMap(null)); polysRef.current = []; };
  }, [map, falde, panelLayout, annotations, geometry, panelPhoto, pdfExport]);

  return null;
}

export default function RoofMapView({ falde, panelLayout, annotations, panelPhoto, pdfExport = false, compact = false, pdfBg = null, origin = null, drawMode = false, nativeFullscreen = true }) {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const all = [];
  (falde || []).forEach((f) => (f.coordinates || []).forEach((c) => all.push(c)));
  if (all.length < 3) return null;
  const center = {
    lat: all.reduce((s, c) => s + c.lat, 0) / all.length,
    lng: all.reduce((s, c) => s + c.lng, 0) / all.length,
  };
  if (hasPdfBg(pdfBg) || drawMode) {
    return <RoofPdfView pdfBg={pdfBg} origin={origin || center} falde={falde} panelLayout={panelLayout} annotations={annotations} panelPhoto={panelPhoto} pdfExport={pdfExport} compact={compact} />;
  }
  if (!apiKey) {
    return (
      <div className="w-full rounded-lg border border-border mb-4 p-6 text-center bg-muted/30" data-testid="roof-map-view-error">
        <Satellite className="h-6 w-6 text-muted-foreground mx-auto mb-2" strokeWidth={1.5} />
        <p className="text-sm text-muted-foreground">Mappa satellitare non disponibile. I dati di superficie e potenza qui sotto restano validi.</p>
      </div>
    );
  }
  return (
    <div className={`w-full ${pdfExport ? (compact ? "h-[300px]" : "h-[430px]") : "h-[340px] sm:h-[420px]"} rounded-lg border border-border mb-4 overflow-hidden`} data-testid="roof-map-view">
      <APIProvider apiKey={apiKey} libraries={["geometry"]}>
        <Map
          reuseMaps
          renderingType="RASTER"
          defaultCenter={center}
          defaultZoom={20}
          mapTypeId="satellite"
          gestureHandling={pdfExport ? "none" : "cooperative"}
          disableDefaultUI={pdfExport}
          mapTypeControl={false}
          streetViewControl={false}
          fullscreenControl={!pdfExport && nativeFullscreen}
          zoomControl={!pdfExport}
          keyboardShortcuts={false}
          tilt={0}
          style={{ width: "100%", height: "100%" }}
        >
          <RoofPolys falde={falde} panelLayout={panelLayout} annotations={annotations} panelPhoto={panelPhoto} pdfExport={pdfExport} />
        </Map>
      </APIProvider>
    </div>
  );
}
